/**
 * Category-specific layout variant definitions and dynamic AI prompt generation.
 * Use generateAIPrompt(category, variant) to build prompts for any category + variant on the fly.
 */

export type RoofSide = 'north' | 'south' | 'east' | 'west' | 'front' | null;

export type LayoutVariant = {
    label: string;
    dir: 'port' | 'land' | 'both' | null;
    minEff: number | null;
    side?: RoofSide;
};

/** Main catalog card uses layout key `default`; variants still generate under this label. */
export const ONE_DRAW_DEFAULT_MAXFILL_CATEGORY = 'oneDRAW Default Maxfill';

export const categoryLayoutVariantDefs: Record<string, LayoutVariant[]> = {
    [ONE_DRAW_DEFAULT_MAXFILL_CATEGORY]: [
        { label: 'Fill all portrait', dir: 'port', minEff: null },
        { label: 'Fill all landscape', dir: 'land', minEff: null },
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
    ],
    'Max Production with Best Roofs': [
        { label: 'Fill all portrait', dir: 'port', minEff: null },
        { label: 'Fill all landscape', dir: 'land', minEff: null },
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        { label: 'Same layout – keep ≥80% efficiency', dir: null, minEff: 80 },
        { label: 'Same layout – keep ≥70% efficiency', dir: null, minEff: 70 },
        { label: 'Same layout – keep ≥60% efficiency', dir: null, minEff: 60 },
        { label: 'Mixed orientations (50% min efficiency)', dir: 'both', minEff: 50 },
    ],
    'Best Solar Access': [
        { label: 'Fill all portrait (best access)', dir: 'port', minEff: null },
        { label: 'Fill all landscape (best access)', dir: 'land', minEff: null },
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        { label: 'Keep ≥85% efficiency', dir: null, minEff: 85 },
        { label: 'Keep ≥80% efficiency', dir: null, minEff: 80 },
        { label: 'Keep ≥75% efficiency', dir: null, minEff: 75 },
        { label: 'Mixed orientations (70% min efficiency)', dir: 'both', minEff: 70 },
    ],
    Balanced: [
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        { label: 'Fill all portrait', dir: 'port', minEff: null },
        { label: 'Fill all landscape', dir: 'land', minEff: null },
        { label: 'Same layout – keep ≥80% efficiency', dir: null, minEff: 80 },
        { label: 'Same layout – keep ≥70% efficiency', dir: null, minEff: 70 },
        { label: 'Same layout – keep ≥60% efficiency', dir: null, minEff: 60 },
        { label: 'Mixed orientations (55% min efficiency)', dir: 'both', minEff: 55 },
    ],
    'Maximum Efficiency': [
        { label: 'Same layout – keep ≥85% efficiency', dir: null, minEff: 85 },
        { label: 'Same layout – keep ≥80% efficiency', dir: null, minEff: 80 },
        { label: 'Same layout – keep ≥70% efficiency', dir: null, minEff: 70 },
        { label: 'Same layout – keep ≥60% efficiency', dir: null, minEff: 60 },
        { label: 'Fill high efficiency only (≥85%)', dir: null, minEff: 85 },
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        { label: 'Portrait only (≥70% efficiency)', dir: 'port', minEff: 70 },
    ],
    Aesthetic: [
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        // Directional aesthetics: single-side, single-orientation layouts
        { label: 'North roofs – portrait only', dir: 'port', minEff: null, side: 'north' },
        { label: 'South roofs – portrait only', dir: 'port', minEff: null, side: 'south' },
        { label: 'West roofs – portrait only', dir: 'port', minEff: null, side: 'west' },
        { label: 'East roofs – portrait only', dir: 'port', minEff: null, side: 'east' },
        { label: 'North roofs – landscape only', dir: 'land', minEff: null, side: 'north' },
        { label: 'South roofs – landscape only', dir: 'land', minEff: null, side: 'south' },
        { label: 'West roofs – landscape only', dir: 'land', minEff: null, side: 'west' },
        { label: 'East roofs – landscape only', dir: 'land', minEff: null, side: 'east' },
    ],
    'Highest Multiplier': [
        { label: 'Fill mixed orientations', dir: 'both', minEff: null },
        { label: 'Same layout – keep ≥90% efficiency', dir: null, minEff: 90 },
        { label: 'Same layout – keep ≥85% efficiency', dir: null, minEff: 85 },
        { label: 'Same layout – keep ≥80% efficiency', dir: null, minEff: 80 },
        { label: 'Fill high multiplier (≥85%)', dir: null, minEff: 85 },
        { label: 'Same layout – keep ≥70% efficiency', dir: null, minEff: 70 },
        { label: 'Portrait only (≥80% efficiency)', dir: 'port', minEff: 80 },
        { label: 'Landscape only (≥80% efficiency)', dir: 'land', minEff: 80 },
    ],
};

/**
 * Azimuth ranges (degrees) for each compass direction.
 * Ranges are inclusive on both ends; north wraps across 0°.
 */
const SIDE_AZIMUTH_RANGES: Record<Exclude<RoofSide, 'front' | null>, [number, number][]> = {
    north: [[0, 45], [315, 360]],
    east:  [[45, 135]],
    south: [[135, 225]],
    west:  [[225, 315]],
};

function azimuthInRanges(azimuth: number, ranges: [number, number][]): boolean {
    const a = ((azimuth % 360) + 360) % 360;
    return ranges.some(([lo, hi]) => a >= lo && a <= hi);
}

/**
 * Filter roof face IDs to only those facing the given compass side.
 * For 'front', falls back to south-facing if no explicit front direction is available.
 * @param roofMeshArr - sceneViewer.roofMeshArr (each mesh has .roofFaceId and .oriAng.azimuth)
 * @param roofFaceIds - current set of candidate roof face IDs
 * @param side - compass direction to keep ('north' | 'south' | 'east' | 'west' | 'front')
 * @returns filtered array of roof face IDs; can be empty when no roofs match
 */
export function filterRoofIdsBySide(
    roofMeshArr: { roofFaceId: string; oriAng: { azimuth: number } }[],
    roofFaceIds: string[],
    side: NonNullable<RoofSide>,
): string[] {
    if (!roofMeshArr?.length || !roofFaceIds?.length) return roofFaceIds;

    const ranges = side === 'front'
        ? SIDE_AZIMUTH_RANGES.south
        : SIDE_AZIMUTH_RANGES[side];

    if (!ranges) return roofFaceIds;

    const idSet = new Set(roofFaceIds);
    const filtered = roofMeshArr
        .filter(
            (m) =>
                idSet.has(m.roofFaceId) &&
                typeof m?.oriAng?.azimuth === 'number' &&
                azimuthInRanges(m.oriAng.azimuth, ranges),
        )
        .map((m) => m.roofFaceId);

    return filtered;
}

/**
 * Generate an AI prompt for a given category and variant (orientation + efficiency).
 * Use this to build prompts on the fly without hardcoding per variant.
 */
export function generateAIPrompt(category: string, variant: LayoutVariant): string {
    const lines: string[] = [`Design a solar roof layout to achieve "${category}".`];

    if (variant.label.toLowerCase().includes('portrait') && variant.dir === 'port') {
        lines.push('Place panels in portrait orientation.');
    } else if (variant.label.toLowerCase().includes('landscape') && variant.dir === 'land') {
        lines.push('Place panels in landscape orientation.');
    } else if (variant.label.toLowerCase().includes('mixed')) {
        lines.push('Use mixed orientations across the roof.');
    }

    if (variant.side) {
        const sideLabel = variant.side === 'front' ? 'street-facing (front)' : variant.side;
        lines.push(`Place panels only on the ${sideLabel} side of the home.`);
    }

    if (variant.minEff != null && variant.minEff > 0) {
        if (category.includes('Solar Access')) {
            lines.push(`Ensure at least ${variant.minEff}% solar exposure.`);
        } else {
            lines.push(`Ensure panels maintain at least ${variant.minEff}% efficiency.`);
        }
    }

    if (category.includes('Production')) {
        lines.push('Optimize for maximum production.');
    } else if (category.includes('Efficiency')) {
        lines.push('Prioritize maximum efficiency.');
    } else if (category.includes('Aesthetic')) {
        lines.push('Focus on visual aesthetics and clean layout.');
    } else if (category.includes('System Size')) {
        lines.push('Maximize the total system size.');
    } else if (category.includes('Multiplier')) {
        lines.push('Prioritize high output per panel (multiplier).');
    } else if (category.includes('Solar Access')) {
        lines.push('Prioritize solar access and exposure.');
    } else if (category.includes('Balanced')) {
        lines.push('Balance production, efficiency, and coverage.');
    }

    return lines.join('\n').trim();
}
